'use client'

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useAccount, useDisconnect } from 'wagmi'

interface User {
  email: string
  walletAddress: string | null
  verifiedEmail: boolean
  referralCode?: string | null
  _id?: string; 
  name?: string;
  totalTokens?: number;
  investmentType?: string | null;
  registrationMethod?: string;
  amountInvested?: number;
  referredBy?: string | null;
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  checkWalletAuth: () => Promise<void>
  loginCardUser: (email: string, userId?: string, referralCode?: string) => void
  loginShareholderSession: (userData: Omit<User, 'walletAddress' | 'verifiedEmail'> & { 
    walletAddress?: string | null, 
    verifiedEmail?: boolean, 
    totalTokens?: number, 
    investmentType?: string | null, 
    registrationMethod?: string,
    amountInvested?: number,
    referredBy?: string | null
  }) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  checkWalletAuth: async () => {},
  loginCardUser: () => {},
  loginShareholderSession: () => {},
  logout: () => {},
})

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { address, isConnected } = useAccount()
  const { disconnect } = useDisconnect()

  const checkWalletAuth = useCallback(async () => {
    setIsLoading(true);
    const currentAuthUser = user;

    if (!address || !isConnected) {
      if (currentAuthUser && currentAuthUser.walletAddress !== null) {
        setUser(null);
      }
      setIsLoading(false);
      return;
    }

    if (currentAuthUser && currentAuthUser.walletAddress === null) {
      console.log("AuthContext: Wallet connected while card user session active. Attempting to authenticate wallet...");
    }

    try {
      const response = await fetch('/api/auth/wallet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ walletAddress: address }),
      });

      const data = await response.json();

      if (data.success && data.user) {
        setUser(prevUser => ({
          ...(prevUser || {}),
          ...data.user,
          walletAddress: data.user.walletAddress || address,
          _id: data.user._id || prevUser?._id,
          registrationMethod: data.user.registrationMethod || prevUser?.registrationMethod,
        }));
      } else {
        if (currentAuthUser && currentAuthUser.walletAddress === null) {
          console.log("AuthContext: Wallet not recognized by /api/auth/wallet, preserving existing non-wallet (card) session.");
        } else {
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Authentication error during checkWalletAuth:', error);
      if (currentAuthUser && currentAuthUser.walletAddress === null) {
        console.log("AuthContext: Error checking wallet auth, preserving existing non-wallet (card) session.");
      } else {
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [address, isConnected, setIsLoading]);
  
  const loginCardUser = (email: string, userId?: string, referralCode?: string) => {
    console.log('AuthContext: Logging in card user', { email, userId, referralCode });
    setUser({
      email: email,
      walletAddress: null,
      verifiedEmail: true,
      referralCode: referralCode || null,
      _id: userId,
      name: undefined,
      registrationMethod: 'card_flow',
      totalTokens: undefined,
      investmentType: undefined,
      amountInvested: undefined,
      referredBy: undefined,
    });
    setIsLoading(false);
  };

  const loginShareholderSession = (userData: Omit<User, 'walletAddress' | 'verifiedEmail'> & { 
    walletAddress?: string | null, 
    verifiedEmail?: boolean, 
    totalTokens?: number, 
    investmentType?: string | null, 
    registrationMethod?: string,
    amountInvested?: number,
    referredBy?: string | null
  }) => {
    console.log('AuthContext: Logging in shareholder session', userData);
    setUser({
      email: userData.email,
      walletAddress: userData.walletAddress || null,
      verifiedEmail: userData.verifiedEmail !== undefined ? userData.verifiedEmail : true,
      referralCode: userData.referralCode || null,
      _id: userData._id,
      name: userData.name,
      totalTokens: userData.totalTokens,
      investmentType: userData.investmentType,
      registrationMethod: userData.registrationMethod || 'shareholder_email_login',
      amountInvested: userData.amountInvested,
      referredBy: userData.referredBy,
    });
    setIsLoading(false);
  };

  const logout = () => {
    disconnect()
    setUser(null)
    console.log('AuthContext: User logged out');
  }

  useEffect(() => {
    if (isConnected && address) {
      checkWalletAuth();
    } else {
      if (user && user.walletAddress !== null) {
        setUser(null);
        console.log('AuthContext: Wallet disconnected, logging out wallet-based user session.');
      }
      if (!user || user.walletAddress !== null) {
         setIsLoading(false);
      }
    }
  }, [isConnected, address, checkWalletAuth]);

  useEffect(() => {
    if (isLoading && !isConnected && !address && !user) {
        setIsLoading(false);
    }
  }, [isLoading, isConnected, address, user]);

  const contextValue: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    checkWalletAuth,
    loginCardUser,
    loginShareholderSession,
    logout,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)

export default AuthProvider 